import React, { useState, useEffect} from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Grid,
  Divider,
  ThemeProvider,
  createTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import styles from './featuresModal.module.scss';
import { FeaturesModalProps } from '../../../interfaces/salePost';


const theme = createTheme({
  typography: {
    fontFamily: '"LTSaeada", "Roboto", sans-serif',
    h6: { fontWeight: 500 },
    button: { textTransform: 'none' }
  },
  components: {
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: 'rgba(29, 27, 32, 0.5)',
          '&.Mui-checked': { color: '#7100FF' }
        }
      }
    }
  }
});

const FeaturesModal = ({
  open,
  onClose,
  initialSelectedFeatures = [],
  additionalCharacters,
  setAdditionalCharacters,
  setSelectedAdditionalCharacters,
  selectedAdditionalCharacters = []
} : FeaturesModalProps) => {
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(initialSelectedFeatures);

  const handleFeatureChange = (featureId: string) => {
    setSelectedFeatures(prev =>
      prev.includes(featureId)
        ? prev.filter(id => id !== featureId)
        : [...prev, featureId]
    );
  };
  
  const handleSave = () => {
    if (!Array.isArray(additionalCharacters)) {
      return;
    }
  
    const formatted = additionalCharacters
      .map((cat: any) => ({
        name: cat.name,
        code: cat.code,
        value: Array.isArray(cat.value)
          ? cat.value
              .filter((feature: any) => selectedFeatures.includes(feature.code))
              .map((feature: any) => ({
                name: feature.name,
                code: feature.code,
                value: true,
              }))
          : [],
      }))
      .filter((cat) => cat.value.length > 0);
    setSelectedAdditionalCharacters?.(formatted);

    onClose();
  };
  
  const categories = Array.from(new Set(
    additionalCharacters.flatMap(cat => cat.name)
  ));

  useEffect(() => {
    if (open && Array.isArray(selectedAdditionalCharacters)) {
      const selectedCodes = selectedAdditionalCharacters.flatMap((category: any) =>
        (category.value || [])
          .filter((item: any) => item.value === true)
          .map((item: any) => item.code)
      );
      setSelectedFeatures(selectedCodes);
    }
  }, [open, selectedAdditionalCharacters]);
  

  return (
    <ThemeProvider theme={theme}>
      <Modal open={open} onClose={onClose} className={styles.featuresModalContainer}>
        <Box sx={{
          position: 'absolute',
          top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 800, maxWidth: '90%',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          height: '700px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          pt: 3, pb: 3, pl: 1
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
            <Typography variant="h6">Seleccione características adicionales para incluir</Typography>
            <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
          </Box>

          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(113, 0, 255, 0.2)', borderRadius: '4px' }
          }}>
            {additionalCharacters.map((category: any, index: number) => (
              <Box key={category.code} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>{category.name}</Typography>
                <Grid container spacing={2}>
                  {category.value.map((feature: any) => (
                    <Grid item xs={4} md={3} key={feature.code}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedFeatures.includes(feature.code)}
                            onChange={() => handleFeatureChange(feature.code)}
                            size="small"
                          />
                        }
                        label={<Typography variant="body2">{feature.name}</Typography>}
                      />
                    </Grid>
                  ))}
                </Grid>
                {index < additionalCharacters.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, gap: 2, borderTop: '1px solid #eee' }}>
            <Button variant="outlined" onClick={onClose} sx={{ borderRadius: '20px', px: 3 }}>Cancelar</Button>
            <Button variant="contained" onClick={handleSave}
              sx={{ borderRadius: '20px', px: 3, backgroundColor: '#7100FF', '&:hover': { backgroundColor: '#5c00d6' } }}
            >
              Guardar
            </Button>
          </Box>
        </Box>
      </Modal>
    </ThemeProvider>
  );
};

export default FeaturesModal;
